import { css, keyframes } from '@emotion/react';
import { formatDuration } from '@sonematic/shared';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';

import { RehearsalSettingsPopover } from './rehearsal-settings-popover';
import { darkTheme } from '@/theme/dark-theme';
import { spacing } from '@/theme/tokens';

export interface RehearsalHeaderProps {
  state: 'idle' | 'requesting_mic' | 'recording' | 'paused';
  elapsedMs: number;
  isListening: boolean;
  wpm: number;
  fontSize: number;
  lineWidth: number;
  onPause: () => void;
  onResume: () => void;
  onDone: () => void;
  onStartOver: () => void;
  onFontSizeChange: (v: number) => void;
  onLineWidthChange: (v: number) => void;
  debugOpen: boolean;
  onDebugToggle: () => void;
  positioning?: 'fixed' | 'relative';
}

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

const isRecordingActive = (state: RehearsalHeaderProps['state']) =>
  state === 'recording' || state === 'paused';

export function RehearsalHeader({
  state,
  elapsedMs,
  isListening,
  wpm,
  fontSize,
  lineWidth,
  onPause,
  onResume,
  onDone,
  onStartOver,
  onFontSizeChange,
  onLineWidthChange,
  debugOpen,
  onDebugToggle,
  positioning = 'fixed',
}: RehearsalHeaderProps) {
  return (
    <div
      css={css`
        position: ${positioning};
        top: 0;
        left: 0;
        right: 0;
        height: 56px;
        z-index: 10;
        background: ${state === 'idle' ? darkTheme.background : darkTheme.surface};
        border-bottom: 1px solid ${state === 'idle' ? 'transparent' : darkTheme.border};
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0 ${spacing.lg};
      `}
    >
      {/* Left section — Logo + Back to Editor button */}
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 4px;
        `}
      >
        <Link
          to="/app/projects"
          aria-label="Sonematic home"
          css={css`
            display: flex;
            align-items: center;
            margin-right: ${spacing.sm};
          `}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="2" y="6" width="4" height="12" rx="1" fill={darkTheme.primary} />
            <rect x="8" y="3" width="4" height="18" rx="1" fill={darkTheme.primary} opacity="0.7" />
            <rect x="14" y="8" width="4" height="8" rx="1" fill={darkTheme.primary} opacity="0.5" />
            <rect
              x="20"
              y="5"
              width="4"
              height="14"
              rx="1"
              fill={darkTheme.primary}
              opacity="0.3"
            />
          </svg>
        </Link>
        <button
          type="button"
          onClick={onDone}
          aria-label="Back to Editor"
          css={css`
            background: none;
            border: none;
            color: ${darkTheme.textSecondary};
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 4px;
            font-size: 14px;
            padding: ${spacing.sm};

            &:hover {
              color: ${darkTheme.text};
            }
          `}
        >
          <ArrowLeft size={20} aria-hidden="true" />
          Back to Editor
        </button>
      </div>

      {/* Center section */}
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: ${spacing.sm};
        `}
      >
        {isRecordingActive(state) && (
          <>
            <div
              data-testid="mic-indicator"
              css={css`
                width: 12px;
                height: 12px;
                border-radius: 50%;
                background: ${isListening ? darkTheme.success : darkTheme.textMuted};
                ${isListening
                  ? css`
                      animation: ${pulse} 1.5s ease-in-out infinite;
                    `
                  : ''}
              `}
            />
            <span
              css={css`
                color: ${darkTheme.text};
                font-size: 16px;
                font-variant-numeric: tabular-nums;
              `}
            >
              {formatDuration(elapsedMs)}
            </span>
            <span
              css={css`
                background: ${darkTheme.background};
                padding: 4px 8px;
                border-radius: 4px;
                font-size: 13px;
                color: ${darkTheme.text};
              `}
            >
              {wpm} WPM
            </span>
          </>
        )}
        {state === 'recording' && (
          <Button variant="secondary" size="sm" onClick={onPause}>
            Pause
          </Button>
        )}
        {state === 'paused' && (
          <Button variant="secondary" size="sm" onClick={onResume}>
            Resume
          </Button>
        )}
        {isRecordingActive(state) && (
          <Button variant="ghost" size="sm" onClick={onStartOver}>
            Start Over
          </Button>
        )}
      </div>

      {/* Right section — Settings */}
      <div>
        <RehearsalSettingsPopover
          fontSize={fontSize}
          lineWidth={lineWidth}
          debugOpen={debugOpen}
          onFontSizeChange={onFontSizeChange}
          onLineWidthChange={onLineWidthChange}
          onDebugToggle={onDebugToggle}
        />
      </div>
    </div>
  );
}
